import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { updateDealer, syncDealer } from '../../redux/slices/dealersSlice';
import { useLanguage } from '../../context/LanguageContext';

const DealerDetails = ({ data, onRefetch }) => {
  const dispatch = useDispatch();
  const { t } = useLanguage();
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const handleSync = () => {
    setSyncing(true);
    dispatch(syncDealer(data.id))
      .unwrap()
      .catch(() => {}) // status/error is shown from the refetched row either way
      .finally(() => {
        setSyncing(false);
        onRefetch?.();
      });
  };

  const startEdit = () => {
    setForm({
      name: data.name || '',
      nickname: data.nickname || '',
      contact_person: data.contact_person || '',
      email: data.email || '',
      tel: data.tel || '',
      street: data.street || '',
      zip: data.zip || '',
      city: data.city || '',
      templateID: data.templateID ?? '',
    });
    setIsEditing(true);
  };

  const handleSave = (e) => {
    e.preventDefault();
    const payload = { ...form, templateID: form.templateID === '' ? null : Number(form.templateID) };
    dispatch(updateDealer({ id: data.id, data: payload }))
      .unwrap()
      .then(() => {
        setIsEditing(false);
        onRefetch?.();
      })
      .catch((err) => alert(err?.error || t('dealers.saveFailed')));
  };

  if (isEditing) {
    return (
      <form onSubmit={handleSave} className='space-y-4'>
        <div className='grid grid-cols-3 gap-4'>
          <div className='col-span-2'>
            <label className='block text-sm font-medium text-gray-700 mb-2'>{t('common.name')} *</label>
            <input
              type='text'
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className='input-field'
              required
            />
          </div>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>{t('dealers.templateId')}</label>
            <input
              type='number'
              value={form.templateID}
              onChange={(e) => setForm({ ...form, templateID: e.target.value })}
              className='input-field'
            />
            {form.templateID !== '' && (
              <p className='text-xs text-gray-500 mt-1'>
                {t('dealerDetails.templateSyncNote')}
              </p>
            )}
          </div>
        </div>
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-2'>{t('dealers.nickname')}</label>
          <input
            type='text'
            value={form.nickname}
            onChange={(e) => setForm({ ...form, nickname: e.target.value })}
            className='input-field'
          />
        </div>
        <div className='grid grid-cols-2 gap-4'>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>{t('dealers.contactPerson')}</label>
            <input
              type='text'
              value={form.contact_person}
              onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
              className='input-field'
            />
          </div>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>{t('dealers.phone')}</label>
            <input
              type='text'
              value={form.tel}
              onChange={(e) => setForm({ ...form, tel: e.target.value })}
              className='input-field'
            />
          </div>
        </div>
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-2'>{t('dealers.email')}</label>
          <input
            type='email'
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className='input-field'
          />
        </div>
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-2'>{t('dealers.street')}</label>
          <input
            type='text'
            value={form.street}
            onChange={(e) => setForm({ ...form, street: e.target.value })}
            className='input-field'
          />
        </div>
        <div className='grid grid-cols-3 gap-4'>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>{t('dealers.zip')}</label>
            <input
              type='text'
              value={form.zip}
              onChange={(e) => setForm({ ...form, zip: e.target.value })}
              className='input-field'
            />
          </div>
          <div className='col-span-2'>
            <label className='block text-sm font-medium text-gray-700 mb-2'>{t('dealers.city')}</label>
            <input
              type='text'
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              className='input-field'
            />
          </div>
        </div>
        <div className='flex justify-end space-x-3 pt-2'>
          <button type='button' onClick={() => setIsEditing(false)} className='btn-secondary'>
            {t('common.cancel')}
          </button>
          <button type='submit' className='btn-primary'>
            {t('dealers.saveChanges')}
          </button>
        </div>
      </form>
    );
  }

  return (
    <div className='space-y-6'>
      <div className='bg-gray-50 rounded-lg p-4'>
        <div className='flex items-center justify-between mb-4'>
          <h3 className='text-lg font-semibold text-gray-900'>{t('dealerDetails.sectionTitle')}</h3>
          <div className='flex items-center space-x-2'>
            {data.templateID && (
              <button onClick={handleSync} disabled={syncing} className='btn-secondary text-sm'>
                {syncing ? t('dealerDetails.syncing') : t('dealerDetails.syncNow')}
              </button>
            )}
            <button onClick={startEdit} className='btn-secondary text-sm'>
              {t('common.edit')}
            </button>
          </div>
        </div>

        {data.templateID && (
          <div className='mb-4 text-xs text-gray-500'>
            {data.last_synced_at ? (
              <span>{t('dealerDetails.lastSynced', { date: new Date(data.last_synced_at).toLocaleString() })}</span>
            ) : (
              <span>{t('dealerDetails.notSyncedYet')}</span>
            )}
            {data.sync_status === 'error' && (
              <p className='text-red-600 mt-1'>{t('dealerDetails.syncFailed', { error: data.sync_error })}</p>
            )}
          </div>
        )}

        <div className='grid grid-cols-2 gap-4'>
          <div>
            <p className='text-sm text-gray-500'>{t('common.name')}</p>
            <p className='font-medium text-gray-900'>{data.name}</p>
          </div>
          <div>
            <p className='text-sm text-gray-500'>{t('dealers.templateId')}</p>
            <p className='font-medium text-gray-900'>{data.templateID ?? '-'}</p>
          </div>
          {data.nickname && (
            <div>
              <p className='text-sm text-gray-500'>{t('dealers.nickname')}</p>
              <p className='font-medium text-gray-900'>{data.nickname}</p>
            </div>
          )}
          <div>
            <p className='text-sm text-gray-500'>{t('dealers.contactPerson')}</p>
            <p className='font-medium text-gray-900'>{data.contact_person || '-'}</p>
          </div>
          <div>
            <p className='text-sm text-gray-500'>{t('dealers.phone')}</p>
            <p className='font-medium text-gray-900'>{data.tel || '-'}</p>
          </div>
          <div>
            <p className='text-sm text-gray-500'>{t('dealers.email')}</p>
            <p className='font-medium text-gray-900'>{data.email || '-'}</p>
          </div>
          <div>
            <p className='text-sm text-gray-500'>{t('dealerDetails.address')}</p>
            {/* Street can be a multi-line block for ERP-synced dealers
                (their "address" maps straight onto our street column, see
                migration 011) -- whitespace-pre-line handles both that and
                the single-line case manual dealers use. */}
            <p className='font-medium text-gray-900 whitespace-pre-line'>
              {data.street || '-'}
              {(data.zip || data.city) && (
                <>
                  <br />
                  {[data.zip, data.city].filter(Boolean).join(' ')}
                </>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DealerDetails;
